/* eslint-disable react/jsx-max-depth */
import React from 'react'
import styled from '@emotion/styled'
import { State, hasLoadedWithoutError } from '@rwwa/rwwa-data-access'
import { SvgLazy } from '@mobi/component-library/Assets/Svg/SvgLazy'
import {
  GiddyUpAcceptors,
  IAcceptorRecord,
} from '@core/Areas/Racing/Components/RacingForm/data-access/giddyUpAcceptors'
import {
  GiddyUpRaces,
  IRaceRecord,
} from '@core/Areas/Racing/Components/RacingForm/data-access/giddyUpRaces'
import { trackGiddyUpShowAllRating } from '@core/Areas/Racing/Components/RacingForm/helpers/analytics'
import {
  getAcceptor,
  isDog,
  isHorse,
  isTrot,
} from '@core/Areas/Racing/Components/RacingForm/integration'
import type { DogAcceptor, IAcceptorEx } from '@core/Areas/Racing/Components/RacingForm/types'
import { observe } from '@core/Components/HOCs/observe'
import { DogColour } from '../../../Common/DogColour'
import { OddsContext, OddsPrice, OddsToggle, OddsType, guOddsAsString } from '../../../Common/Odds'
import { RatingTacho } from '../../../Common/RatingTacho'

const AllRatingComponent: React.FC<{
  raceState: State<IRaceRecord>
  acceptorState: State<IAcceptorRecord>
  raceKey: string
}> = ({ raceState, acceptorState, raceKey }) => {
  const [oddsContextValue, setOddsContextValue] = React.useState<OddsType>('fixed')
  const handleToggleOddsContext = () =>
    setOddsContextValue(curr => (curr === 'fixed' ? 'tote' : 'fixed'))

  const race = GiddyUpRaces.getFrom(raceState, raceKey)

  React.useEffect(trackGiddyUpShowAllRating, [])

  if (!hasLoadedWithoutError(race)) {
    return <div />
  }

  return (
    <OddsContext.Provider
      value={{ currentOdds: oddsContextValue, toggleOdds: handleToggleOddsContext }}
    >
      <div data-tid-all-rating=''>
        <div
          style={{
            boxShadow: '0 2px 2px -2px rgba(0, 0, 0, 0.4)',
            backgroundColor: '#fff',
            marginBottom: 1,
          }}
        >
          <AllRatingHeaderStyled className='l-faux-table l-table-fixed w100 fz-12'>
            <div className='l-faux-row'>
              <div className='l-faux-cell l-cell-xs-6 text-left pl10' />
              <div className='l-faux-cell l-cell-xs-2 text-center alignmid'>
                <span>
                  <SvgLazy name='GiddyUpLogo' height='1.4rem' color='#0051A0' />
                </span>
              </div>
              <div className='l-faux-cell l-cell-xs-2 text-center'>
                <OddsToggle />
              </div>
            </div>
          </AllRatingHeaderStyled>
        </div>

        {race.sortedAcceptorKeys.toArray().map(starterKey => {
          const acceptor = starterKey && GiddyUpAcceptors.getFrom(acceptorState, starterKey)

          if (!acceptor) {
            return <span key='missing'>Could not load acceptor!</span>
          }

          if (!hasLoadedWithoutError(acceptor)) {
            return <span key='error' />
          }

          const extraInfo = getAcceptor(acceptor.key, acceptor.acceptorNumber)
          if (!extraInfo) {
            return <span key='no-info'>Could not match acceptor</span>
          }

          return (
            <div key={starterKey} style={{ maxWidth: '64em', margin: '0 auto' }}>
              <AcceptorCardStyled className={isTrot(extraInfo) ? 'trotRow' : ''}>
                <AcceptorCard acceptor={acceptor} extraInfo={extraInfo} />
              </AcceptorCardStyled>
            </div>
          )
        })}
      </div>
    </OddsContext.Provider>
  )
}

const state$ = Rx.Observable.combineLatest(
  GiddyUpRaces.data$,
  GiddyUpAcceptors.data$,
  (raceState, acceptorState) => ({ raceState, acceptorState })
)

export const AllRating = observe<
  { raceState: State<IRaceRecord>; acceptorState: State<IAcceptorRecord> },
  { raceKey: string }
>(state$, AllRatingComponent)

// ================
// Local Components
// ================

const AcceptorCard: React.FC<{ acceptor: IAcceptorRecord; extraInfo: IAcceptorEx }> = ({
  acceptor,
  extraInfo,
}) => {
  if (isDog(extraInfo) && acceptor.isGiddyupScratched) {
    return (
      <div
        data-tid-scratched-acceptor
        className='l-faux-table l-table-fixed w100 fz-12'
        style={{ color: '#888' }}
      >
        <div className='l-faux-row'>
          <div className='l-faux-cell l-cell-xs-6 lh1'>
            <DogAcceptorName
              className='l-faux-cell l-cell-xs-12 text-left pl10 fz-14 faux-strong'
              extraInfo={extraInfo}
              acceptorNumber={acceptor.acceptorNumber}
            />
          </div>
          <div
            data-tid-scratched-keyword=''
            className='l-faux-cell l-cell-xs-6 text-center fz-12 faux-strong red alignmid'
          >
            SCRATCHED
          </div>
        </div>
      </div>
    )
  }

  if (isDog(extraInfo)) {
    return <DogAcceptorCard acceptor={acceptor} extraInfo={extraInfo} />
  }

  if (acceptor.isGiddyupScratched) {
    return (
      <div
        data-tid-scratched-acceptor
        className='l-faux-table l-table-fixed w100 fz-12'
        style={{ color: '#888' }}
      >
        <div className='l-faux-row'>
          <div className='l-faux-cell l-cell-xs-1 text-center'>
            <span data-tid-acceptor-number='' className='starterNumber pbhalfem'>
              {acceptor.acceptorNumber}
            </span>
          </div>
          <div className='l-faux-cell l-cell-xs-5 text-center alignmid'>
            <div className='l-faux-table l-table-fixed w100 fz-11'>
              <div className='l-faux-row'>
                <AcceptorName
                  className='l-faux-cell l-cell-xs-12 text-left pl10 fz-14 faux-strong'
                  extraInfo={extraInfo}
                />
              </div>
            </div>
          </div>
          <div
            data-tid-scratched-keyword=''
            className='l-faux-cell l-cell-xs-6 text-center fz-12 faux-strong red'
          >
            SCRATCHED
          </div>
        </div>
      </div>
    )
  }

  if (isHorse(extraInfo)) {
    return (
      <AcceptorCardComponent acceptor={acceptor} extraInfo={extraInfo}>
        <div data-tid-position='' className='l-faux-cell l-cell-xs-12 text-left'>
          <div className='l-faux-table w100'>
            <div className='l-faux-cell l-cell-xs-4 aligntop'>
              <span className='faux-strong'>Br: </span>
              {extraInfo.barrier}
            </div>
            <div className='l-faux-cell l-cell-xs-8 lh1.5'>
              {acceptor.speedMapDesc && (
                <span className='dib'>{` ${humanizeSpeedMapDescription(acceptor.speedMapDesc)}`}</span>
              )}
            </div>
          </div>
        </div>
      </AcceptorCardComponent>
    )
  }

  if (isTrot(extraInfo)) {
    return (
      <AcceptorCardComponent acceptor={acceptor} extraInfo={extraInfo}>
        <div data-tid-position='' className='l-faux-cell l-cell-xs-12 text-left'>
          <span className='faux-strong'>Hcp: </span>
          <span className='pr20'>{extraInfo.handicap}</span>
          {acceptor.speedMapDesc && (
            <span className='dib'>{` ${humanizeSpeedMapDescription(acceptor.speedMapDesc)}`}</span>
          )}
        </div>
      </AcceptorCardComponent>
    )
  }

  return <span>Details are not available.</span>
}

const AcceptorCardComponent: React.FC<{
  acceptor: IAcceptorRecord
  extraInfo: IAcceptorEx
  className?: string
  children?: React.ReactNode
}> = ({ acceptor, extraInfo, children, className }) => {
  const silk = extraInfo.silkImage

  return (
    <div data-tid-acceptor-details='' className={`l-faux-table l-table-fixed w100 ${className}`}>
      <div className='l-faux-row'>
        <div className='l-faux-cell l-cell-xs-1 text-center aligntop fz-12'>
          <span data-tid-acceptor-number='' className='starterNumber pbhalfem'>
            {acceptor.acceptorNumber}
          </span>
          <span
            data-tid-silk=''
            className='silk'
            style={{ background: silk.cssurl, width: silk.width }}
          />
        </div>
        <div className='l-faux-cell l-cell-xs-11 text-left aligntop pl5'>
          <div className='l-faux-table l-table-fixed w100'>
            <div className='l-faux-row'>
              <div className='l-faux-cell l-cell-xs-6 text-center aligntop'>
                <div className='l-faux-table l-table-fixed w100 fz-11'>
                  <div className='l-faux-row'>
                    <AcceptorName
                      className='l-faux-cell l-cell-xs-12 text-left fz-14 faux-strong lh1 pb5'
                      extraInfo={extraInfo}
                    />
                  </div>
                  <div className='l-faux-row'>{children}</div>
                </div>
              </div>
              <div className='l-faux-cell l-cell-xs-2 text-center fz-12 aligntop gupprice'>
                <span data-tid-required-odds=''>{guOddsAsString(acceptor.requiredOdds)}</span>
                <RatingTacho rating={acceptor.rating} />
              </div>
              <div className='l-faux-cell l-cell-xs-2 text-center fz-12 faux-strong aligntop'>
                <OddsPrice acceptor={extraInfo} />
              </div>
            </div>
          </div>
          <CommentTable acceptor={acceptor} />
        </div>
      </div>
    </div>
  )
}

const DogAcceptorCard: React.FC<{
  acceptor: IAcceptorRecord
  extraInfo: DogAcceptor
}> = ({ acceptor, extraInfo }) => {
  const isReserveAndBeingUsed = (dogToCheck: IAcceptorRecord) =>
    dogToCheck.startingPosition !== 0 && dogToCheck.startingPosition !== dogToCheck.acceptorNumber

  return (
    <div data-tid-acceptor-details className='l-faux-table l-table-fixed w100 fz-12'>
      <div className='l-faux-row'>
        <div className='l-faux-cell l-cell-xs-11 text-left aligntop pl10'>
          <div className='l-faux-table l-table-fixed w100 fz-12'>
            <div className='l-faux-row'>
              <div className='l-faux-cell l-cell-xs-6 text-center aligntop'>
                <div className='l-faux-table l-table-fixed w100 fz-11'>
                  <div className='l-faux-row'>
                    <DogAcceptorName
                      className='l-faux-cell l-cell-xs-12 text-left fz-14 faux-strong lh1 pb5'
                      extraInfo={extraInfo}
                      acceptorNumber={acceptor.acceptorNumber}
                    />
                  </div>
                </div>
              </div>

              <div className='l-faux-cell l-cell-xs-2 text-center fz-12 aligntop gupprice'>
                <span data-tid-required-odds=''>{guOddsAsString(acceptor.requiredOdds)}</span>
                <RatingTacho rating={acceptor.rating} />
              </div>
              <div className='l-faux-cell l-cell-xs-2 text-center fz-12 faux-strong'>
                <OddsPrice acceptor={extraInfo} />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className='l-faux-row'>
        <div className='l-faux-table l-table-fixed w75 fz-11'>
          {isReserveAndBeingUsed(acceptor) && (
            <div className='l-faux-row'>
              <div data-tid-box className='l-faux-cell l-cell-xs-6 text-left pl10'>
                <span className='faux-strong'>Box: </span>
                {acceptor.startingPosition}
              </div>
            </div>
          )}
          <div className='l-faux-row'>
            <div data-tid-best-time className='l-faux-cell l-cell-xs-4 text-left pl10'>
              <span className='faux-strong'>Best: </span>
              {acceptor.bestTimeThisTrackAndDistance}
            </div>
            <div data-tid-early-speed className='l-faux-cell l-cell-xs-4 text-left pl10'>
              <span className='faux-strong'>Early Speed: </span>
              {acceptor.sectionalTime}
            </div>
          </div>
          <div className='l-faux-row'>
            <div data-tid-jump className='l-faux-cell l-cell-xs-4 text-left pl10'>
              <span className='faux-strong'>Jump: </span>
              {acceptor.jumpDirection}
            </div>
            <div data-tid-run-line='' className='l-faux-cell l-cell-xs-4 text-left pl10'>
              <span className='faux-strong'>Run Line: </span>
              {acceptor.preferredTrackPosition}
            </div>
          </div>
        </div>
      </div>
      <CommentTable acceptor={acceptor} />
    </div>
  )
}

const CommentTable: React.FC<{ acceptor: IAcceptorRecord }> = ({ acceptor }) => {
  return (
    <div className='l-faux-table l-table-fixed w100 fz-11 lh1.5 pl10'>
      <div className='l-faux-row'>
        <div data-tid-comment='' className='l-faux-cell l-cell-xs-12 text-left'>
          {acceptor.comment}
        </div>
      </div>
    </div>
  )
}

const AcceptorName: React.FC<{ extraInfo: IAcceptorEx; className: string }> = ({
  extraInfo,
  className,
}) => {
  return (
    <div
      data-tid-acceptor-name=''
      className={className}
      style={{ wordBreak: 'break-word', hyphens: 'auto' }}
    >
      <div style={{ fill: '#fff' }}>
        {extraInfo.name}
        {extraInfo.isEmergency ? ` (EM${extraInfo.emergencyNum})` : null}
      </div>
    </div>
  )
}

const DogAcceptorName: React.FC<{
  extraInfo: IAcceptorEx
  className: string
  acceptorNumber: number
}> = ({ extraInfo, className, acceptorNumber }) => {
  return (
    <div className={className}>
      <div className='l-faux-table l-table-fixed w100'>
        <div className='l-faux-row'>
          <div className='l-faux-cell l-cell-xs-2 aligntop' data-tid-acceptor-number>
            <div style={{ maxWidth: '2.4em' }}>
              <DogColour acceptorNumber={acceptorNumber} />
            </div>
          </div>

          <div className='l-faux-cell l-cell-xs-10 pl5 alignmid'>
            <div data-tid-acceptor-name style={{ fill: '#fff' }}>
              {extraInfo.name}
              {extraInfo.isEmergency ? ` (RES${extraInfo.emergencyNum})` : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// =============
// Local Helpers
// =============

function humanizeSpeedMapDescription(descriptor: string) {
  if (!descriptor) {
    return ''
  }

  switch (descriptor.toUpperCase()) {
    case 'LDR':
      return 'Leader'
    case 'FWD':
      return 'Forward'
    case 'FOM':
      return 'Forward of Midfield'
    case 'MID':
      return 'Midfield'
    case 'WTM':
      return 'Worse than Midfield'
    case 'BAC':
      return 'Back'
    case 'REA':
      return 'Rear'
    default:
      return ''
  }
}

// ======
// Styles
// ======

const AllRatingHeaderStyled = styled.div({
  color: '#552d6b',
  fontWeight: 'bold',
  padding: '0.5em',
  fontSize: '1.4em',
  marginBottom: 1,
  paddingLeft: '1.3em',

  '.winprice': {
    color: '#000',
    fontWeight: 'normal',
  },

  '.winprice::after': {
    marginTop: '-1em',
  },
})

const AcceptorCardStyled = styled.div({
  padding: '1em 0.5em',
  borderBottom: '1px solid #e8e8e8',
  backgroundColor: '#fff',

  '.gupprice': {
    paddingTop: '0.2rem',
    color: '#004e9d',
    fontWeight: 'bold',
  },

  '&.trotRow': {
    marginBottom: '-0.5em',

    '.silk': {
      marginTop: '-0.5em',
    },
  },

  '.starterNumber': {
    display: 'block',
    fontWeight: 600,
    lineHeight: '1.2em',
    margin: 0,
    textAlign: 'center',
  },

  '.silk': {
    display: 'inline-block',
    height: '2.5em',
    marginTop: '0.0833em', // 1px
    width: '2.1em',
  },
})
